import { memo, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTickerStore } from '../stores/tickerStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useConnectionStore } from '../stores/connectionStore';
import { useFavouritesStore } from '../stores/favouritesStore';
import { useSymbolStore } from '../stores/symbolStore';
import { formatPrice, formatPct } from '../utils/formatters';
import {
  FOOTER_FILTERS,
  tickerMatchesFooterFilter,
} from '../utils/footerFilter';
import type { TickerEntry } from '../stores/tickerStore';
import type { FooterFilter } from '../utils/footerFilter';
import type { MarketId } from '../components/common/LockedState';

/**
 * Compute the ticker strip layout. When `filter === 'FAV'` all visible
 * entries are favourites; otherwise we bubble up favourites to the front
 * (with a visual separator), then fill the rest from the quote-filter.
 * Volume sort applies only to the non-favourite segment.
 */
function computeStrip(
  tickers: Record<string, TickerEntry>,
  filter: FooterFilter,
  favouriteSymbols: Set<string>,
  marketOf: (symbol: string) => MarketId,
  selectedMarket: MarketId,
): { favs: TickerEntry[]; rest: TickerEntry[] } {
  const matching = Object.values(tickers).filter(
    (t) =>
      t.lastPrice &&
      tickerMatchesFooterFilter(t, filter, favouriteSymbols) &&
      (selectedMarket === 'spot' || marketOf(t.symbol) === selectedMarket),
  );

  if (filter === 'FAV') {
    return { favs: matching.slice(0, 15), rest: [] };
  }

  const notional = (t: TickerEntry) =>
    parseFloat(t.volume24h ?? '0') * parseFloat(t.lastPrice ?? '0');
  const favs = matching
    .filter((t) => favouriteSymbols.has(t.symbol))
    .sort((a, b) => notional(b) - notional(a));
  const rest = matching
    .filter((t) => !favouriteSymbols.has(t.symbol))
    .sort((a, b) => notional(b) - notional(a));

  // Keep total ≈ 10-12 items visible so the strip stays dense but not crowded.
  const favLimit = Math.min(favs.length, 6);
  const restLimit = Math.max(6, 12 - favLimit);
  return { favs: favs.slice(0, favLimit), rest: rest.slice(0, restLimit) };
}

type StripItem =
  | { kind: 'ticker'; ticker: TickerEntry; group: 'fav' | 'rest' }
  | { kind: 'separator' };

function makeItems(favs: TickerEntry[], rest: TickerEntry[]): StripItem[] {
  const items: StripItem[] = [];
  for (const t of favs) items.push({ kind: 'ticker', ticker: t, group: 'fav' });
  if (favs.length > 0 && rest.length > 0) items.push({ kind: 'separator' });
  for (const t of rest)
    items.push({ kind: 'ticker', ticker: t, group: 'rest' });
  return items;
}

const TickerStrip = memo(
  function TickerStrip({
    items,
    onSelect,
  }: {
    items: StripItem[];
    itemsKey: string;
    onSelect: (symbol: string) => void;
  }) {
    const render = (item: StripItem, i: number) => {
      if (item.kind === 'separator') {
        return (
          <span
            key={`sep-${i}`}
            aria-hidden
            className="flex items-center flex-shrink-0 select-none"
          >
            <span className="w-px h-3 bg-border" />
          </span>
        );
      }
      const t = item.ticker;
      const pct = t.change24hPct ? parseFloat(t.change24hPct) : 0;
      const pctColor =
        pct > 0 ? 'text-bullish' : pct < 0 ? 'text-bearish' : 'text-text-muted';
      const star = item.group === 'fav';
      return (
        <button
          key={`${t.symbol}-${i}`}
          type="button"
          data-sym={t.symbol}
          onClick={() => onSelect(t.symbol)}
          className="flex items-center gap-1.5 flex-shrink-0 font-mono hover:text-text-primary transition-colors"
        >
          {star && (
            <span
              className="text-yellow-400 leading-none"
              aria-label="Favourite"
            >
              ★
            </span>
          )}
          <span className="text-text-secondary">{t.wsSymbol ?? t.symbol}</span>
          <span className="tp text-text-primary">
            {formatPrice(t.lastPrice ?? '0')}
          </span>
          <span className={`tc ${pctColor}`}>{formatPct(pct)}</span>
        </button>
      );
    };

    // Duplicate the list so the CSS `ticker-scroll` animation loops seamlessly.
    return (
      <>
        {items.map((item, i) => render(item, i))}
        {items.map((item, i) => render(item, items.length + i))}
      </>
    );
  },
  (prev, next) =>
    prev.itemsKey === next.itemsKey && prev.onSelect === next.onSelect,
);

function ConnectionDot() {
  const status = useConnectionStore((s) => s.status);
  const latencyMs = useConnectionStore((s) => s.latencyMs);
  const dotColor =
    status === 'connected'
      ? 'bg-bullish'
      : status === 'connecting' || status === 'reconnecting'
        ? 'bg-warning'
        : 'bg-bearish';
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0 text-text-muted">
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      {latencyMs !== null && <span className="font-mono">{latencyMs}ms</span>}
    </div>
  );
}

export function Footer() {
  const navigate = useNavigate();
  const tickers = useTickerStore((s) => s.tickers);
  const footerQuoteFilter = useSettingsStore((s) => s.footerQuoteFilter);
  const setFooterQuoteFilter = useSettingsStore((s) => s.setFooterQuoteFilter);
  const selectedMarket = useSettingsStore((s) => s.selectedMarket);
  const favourites = useFavouritesStore((s) => s.favourites);
  const available = useSymbolStore((s) => s.available);
  const active = useSymbolStore((s) => s.active);

  const scrollRef = useRef<HTMLDivElement>(null);

  const favouriteSymbols = useMemo(() => new Set(favourites), [favourites]);

  // Resolve a ticker's market from the symbol store. Fall back to 'spot' —
  // which matches today's reality (everything is spot) and keeps the filter
  // a no-op for the default market.
  const marketOf = useMemo(() => {
    const index: Record<string, MarketId> = {};
    const write = (sym: string, m?: string) => {
      if (m === 'margin' || m === 'futures') index[sym] = m;
      else index[sym] = 'spot';
    };
    for (const s of available) write(s.symbol, s.market);
    for (const a of active) write(a.symbol, a.market);
    return (symbol: string): MarketId => index[symbol] ?? 'spot';
  }, [available, active]);

  const { favs, rest } = useMemo(
    () =>
      computeStrip(
        tickers,
        footerQuoteFilter,
        favouriteSymbols,
        marketOf,
        selectedMarket,
      ),
    [tickers, footerQuoteFilter, favouriteSymbols, marketOf, selectedMarket],
  );

  const items = useMemo(() => makeItems(favs, rest), [favs, rest]);
  const itemsKey = useMemo(
    () =>
      items
        .map((i) => (i.kind === 'separator' ? '|' : i.ticker.symbol))
        .join(','),
    [items],
  );

  const onSelect = useMemo(
    () => (symbol: string) => {
      navigate(`/chart/${symbol}`);
    },
    [navigate],
  );

  // Patch prices/pct in place without touching the DOM tree structure,
  // so the CSS animation continues uninterrupted.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    for (const item of items) {
      if (item.kind === 'separator') continue;
      const t = item.ticker;
      const nodes = el.querySelectorAll<HTMLButtonElement>(
        `[data-sym="${t.symbol}"]`,
      );
      const priceText = formatPrice(t.lastPrice ?? '0');
      const pct = t.change24hPct ? parseFloat(t.change24hPct) : 0;
      const pctText = formatPct(pct);
      const pctClass =
        pct > 0 ? 'text-bullish' : pct < 0 ? 'text-bearish' : 'text-text-muted';

      for (const node of nodes) {
        const priceEl = node.querySelector<HTMLSpanElement>('.tp');
        const pctEl = node.querySelector<HTMLSpanElement>('.tc');
        if (priceEl && priceEl.textContent !== priceText)
          priceEl.textContent = priceText;
        if (pctEl) {
          if (pctEl.textContent !== pctText) pctEl.textContent = pctText;
          const want = `tc ${pctClass}`;
          if (pctEl.className !== want) pctEl.className = want;
        }
      }
    }
  }, [items]);

  return (
    <footer className="flex items-center bg-footer-bg border-t border-border h-8 px-3 text-xs gap-4 overflow-hidden">
      {/* Quote / FAV filter toggle */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {FOOTER_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFooterQuoteFilter(f)}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
              footerQuoteFilter === f
                ? 'bg-accent text-white'
                : 'text-text-muted hover:text-text-secondary'
            }`}
            title={f === 'FAV' ? 'Show starred symbols only' : `Filter by ${f}`}
          >
            {f === 'FAV' ? '★' : f}
          </button>
        ))}
      </div>

      <div className="w-px h-4 bg-border flex-shrink-0" />

      {/* Animated ticker scroll */}
      <div className="flex-1 overflow-hidden">
        {items.length > 0 ? (
          <div
            key={itemsKey}
            ref={scrollRef}
            className="ticker-scroll flex items-center gap-6"
          >
            <TickerStrip
              items={items}
              itemsKey={itemsKey}
              onSelect={onSelect}
            />
          </div>
        ) : (
          <span className="text-text-muted">
            {footerQuoteFilter === 'FAV'
              ? 'Star a symbol to see it here'
              : 'No ticker data'}
          </span>
        )}
      </div>

      <div className="w-px h-4 bg-border flex-shrink-0" />
      <a
        href="https://www.kraken.com"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-[10px] transition-colors flex-shrink-0 hidden lg:flex"
        style={{ color: '#5741D9' }}
        title="Market data from Kraken"
      >
        <span>Market data from</span>
        <img src="/Kraken-Emblem.png" alt="Kraken" className="h-9 w-auto" />
        <svg
          viewBox="0 0 12 12"
          className="w-2.5 h-2.5 opacity-70"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            d="M5 2H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V7M7 1h4m0 0v4m0-4L5 7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </a>
      <div className="w-px h-4 bg-border flex-shrink-0 hidden lg:block" />
      <ConnectionDot />
    </footer>
  );
}
